import { TableFooter } from '@fukuri-admins/features/Table/TableFooter/index.tsx';
import { TableSearch } from '@fukuri-admins/ui/common';
import { CircularProgress } from '@mui/material';
import AddClientAdminUserModal from '../../../components/clientAdminUser/AddClientAdminUserModal/index.tsx';
import { ClientAdminUserTableContent } from '../../../components/clientAdminUser/ClientAdminUserTableContent/index.tsx';
import { ClientAdminUserTableFilter } from '../../../components/clientAdminUser/ClientAdminUserTableFilter/index.tsx';
import { Button } from '../../../components/common/Button/index.tsx';
import { ContentsHeader } from '../../../components/common/ContentsHeader/index.tsx';
import MessageModal from '../../../components/common/MessageModal/index.tsx';
import { NewClientAdminUserFormType } from '../../../types/forms/NewClientAdminUserFormType.ts';
import { ClientAdminUserType } from '../../../types/resources/ClientAdminUserType.ts';
import { ActionTableColumnsType } from '../../../types/table/ActionTableColumnsType.ts';
import { ActionTableSortParamsType } from '../../../types/table/ActionTableSortParamsType.ts';
import { handleSorting } from '../../../utils/sortings.ts';
import styles from './styles.module.scss';

type StandardClientAdminUserListProps = {
  filteredClientAdminUsers: ClientAdminUserType[];
  setFilteredClientAdminUsers: (users: ClientAdminUserType[]) => void;
  isLoading: boolean;
  handlePageChange: (page: number) => void;
  totalCount: number;
  totalPage: number;
  setAddClientStatus: (status: boolean) => void;
  addClientFinishStatus: boolean;
  setAddClientFinishStatus: (status: boolean) => void;
  handlePreviousRoute: () => void;
  handleAddClientAdminUser: (data: NewClientAdminUserFormType) => Promise<boolean>;
  handleAddClientFinish: () => void;
  errorMessageModalStatus: boolean;
  setErrorMessageModalStatus: (status: boolean) => void;
  errMsg: string;
  sortParams: ActionTableSortParamsType;
  setSortParams: (params: ActionTableSortParamsType) => void;
  handleErrorMessageModal: () => void;
  addClientStatus: boolean;
  creditEvaluationFlag: boolean;
  lastAddedClientAdminUserEmail: string;
  onSearch: () => void;
  onClear: () => void;
};

export const StandardClientAdminUserList = ({
  filteredClientAdminUsers,
  setFilteredClientAdminUsers,
  isLoading,
  handlePageChange,
  totalCount,
  totalPage,
  setAddClientStatus,
  addClientFinishStatus,
  setAddClientFinishStatus,
  handlePreviousRoute,
  handleAddClientAdminUser,
  handleAddClientFinish,
  errorMessageModalStatus,
  setErrorMessageModalStatus,
  errMsg,
  sortParams,
  setSortParams,
  handleErrorMessageModal,
  addClientStatus,
  creditEvaluationFlag,
  lastAddedClientAdminUserEmail,
  onSearch,
  onClear,
}: StandardClientAdminUserListProps) => {
  const tableHeader: ActionTableColumnsType[] = [
    {
      label: '利用企業名',
      key: 'client.name',
      sortable: true,
      filterable: true,
    },
    { label: '氏名', key: 'full_name', sortable: true, filterable: true },
    {
      label: 'メールアドレス',
      key: 'email',
      sortable: true,
      filterable: true,
    },
    {
      label: '作成日時',
      key: 'created_at',
      sortable: true,
      filterable: false,
    },
    { label: '削除', key: 'delete', sortable: false, filterable: false },
  ];

  return (
    <div className={styles.Root} data-testid='client-admin-page'>
      <ContentsHeader
        title='利用企業管理者一覧'
        description='利用企業管理者の追加・編集・削除ができます。'
        actions={
          <Button
            title='追加する'
            onClick={() => {
              setAddClientStatus(true);
            }}
            data-testid='add-client-admin-button'
          />
        }
      />
      <div className={styles.tableContainer}>
        <div className={styles.Root__Table}>
          <div className={styles.searchContainer}>
            <TableSearch onSearch={onSearch} onClear={onClear}>
              <ClientAdminUserTableFilter />
            </TableSearch>
          </div>
          {isLoading ? (
            <div className={styles.loading}>
              <CircularProgress size='1rem' color='inherit' />
            </div>
          ) : (
            <>
              <ClientAdminUserTableContent
                rows={filteredClientAdminUsers}
                columns={tableHeader}
                sortParams={sortParams}
                headerSort={column =>
                  handleSorting(
                    column,
                    sortParams,
                    filteredClientAdminUsers,
                    setSortParams,
                    setFilteredClientAdminUsers
                  )
                }
              />
              <TableFooter
                onPageChange={handlePageChange}
                totalCount={totalCount}
                totalPage={totalPage}
                itemCount={filteredClientAdminUsers.length}
              />
            </>
          )}
        </div>
      </div>
      <AddClientAdminUserModal
        show={addClientStatus}
        onClose={handlePreviousRoute}
        action={handleAddClientAdminUser}
        creditEvaluationFlag={creditEvaluationFlag}
      />
      <MessageModal
        title={'企業管理者の追加が完了しました。'}
        description={
          creditEvaluationFlag
            ? '企業の与信が完了している場合、管理画面のご案内メールが管理者様宛に送信されます。'
            : `管理画面のご案内メールが${lastAddedClientAdminUserEmail}宛に送信されました。`
        }
        show={addClientFinishStatus}
        onClose={() => setAddClientFinishStatus(false)}
        action={handleAddClientFinish}
      />
      <MessageModal
        title='企業管理者の追加エラー'
        description={`企業管理者の追加エラーが発生しました。\n\n<<エラーメッセージ>>\n${errMsg}`}
        show={errorMessageModalStatus}
        onClose={() => setErrorMessageModalStatus(false)}
        action={handleErrorMessageModal}
        data-testid='client-admin-user-error-modal'
      />
    </div>
  );
};
